import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import {
  ShieldWarning, User, Envelope, Prohibit, LinkSimple, Warning, Info,
} from "@phosphor-icons/react";

/**
 * Admin → Customer Portal Access Alerts
 * -------------------------------------
 * Read-only tile showing the last 30 days of Phase 1 + Phase 2 portal
 * audit signals (ambiguous / unknown / disabled / auto-linked / added /
 * activation regenerated / cross-customer blocks). Every backend call
 * here is a GET; the tile never writes.
 */
export default function PortalAlerts() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    (async () => {
      try {
        const [s, e] = await Promise.all([
          api.get(`/admin/customer-portal-alerts/summary?days=${days}`),
          api.get(`/admin/customer-portal-alerts/events?days=${days}${filter ? `&action=${encodeURIComponent(filter)}` : ""}`),
        ]);
        if (!alive) return;
        setSummary(s.data);
        setEvents(e.data.events || []);
      } catch (ex) {
        if (!alive) return;
        setErr(ex?.response?.data?.detail || "Could not load access alerts.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [days, filter]);

  return (
    <div className="p-6 space-y-5" data-testid="portal-alerts-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
            Customer Portal Access Alerts
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Read-only signals from the last {days} days: ambiguous emails,
            denied logins, auto-linked users, additions, activation-link
            rotations, and blocked cross-customer adds. This panel never
            writes to any record.
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-lg border border-slate-300 text-sm px-3 py-2"
          data-testid="portal-alerts-days"
        >
          {[7, 14, 30, 60, 90].map(d => <option key={d} value={d}>Last {d} days</option>)}
        </select>
      </header>

      {err && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 flex items-start gap-2">
          <Warning size={16} weight="bold" className="mt-[1px]" /> {err}
        </div>
      )}

      {loading ? (
        <div className="text-slate-500 text-sm" data-testid="portal-alerts-loading">Loading…</div>
      ) : (
        <>
          {/* summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="portal-alerts-summary">
            <SummaryCard
              icon={<ShieldWarning size={18} weight="bold" />}
              tone="rose"
              label="Ambiguous emails"
              value={countMany(summary?.counts, ["customer_portal:ambiguous_customer_users", "customer_portal:ambiguous_customer_fallback", "customer_portal:ambiguous_after_race"])}
              onClick={() => setFilter("customer_portal:ambiguous_customer_fallback")}
              testId="alert-ambiguous"
            />
            <SummaryCard
              icon={<Prohibit size={18} weight="bold" />}
              tone="amber"
              label="Unknown-email attempts"
              value={countKey(summary?.counts, "customer_portal:denied_unknown_email")}
              onClick={() => setFilter("customer_portal:denied_unknown_email")}
              testId="alert-unknown"
            />
            <SummaryCard
              icon={<Prohibit size={18} weight="bold" />}
              tone="orange"
              label="Disabled-user attempts"
              value={countMany(summary?.counts, ["customer_portal:denied_disabled", "customer_portal:denied_removed"])}
              onClick={() => setFilter("customer_portal:denied_disabled")}
              testId="alert-disabled"
            />
            <SummaryCard
              icon={<LinkSimple size={18} weight="bold" />}
              tone="emerald"
              label="Automatic links"
              value={countKey(summary?.counts, "customer_portal:auto_link_created")}
              onClick={() => setFilter("customer_portal:auto_link_created")}
              testId="alert-autolink"
            />
            <SummaryCard
              icon={<User size={18} weight="bold" />}
              tone="indigo"
              label="New customer users"
              value={countMany(summary?.counts, ["customer_portal:user_added", "customer_portal:user_reactivated"])}
              onClick={() => setFilter("customer_portal:user_added")}
              testId="alert-new-users"
            />
            <SummaryCard
              icon={<Envelope size={18} weight="bold" />}
              tone="slate"
              label="Activation regenerated"
              value={countKey(summary?.counts, "customer_portal:activation_regenerated")}
              onClick={() => setFilter("customer_portal:activation_regenerated")}
              testId="alert-activation"
            />
            <SummaryCard
              icon={<Prohibit size={18} weight="bold" />}
              tone="rose"
              label="Cross-customer add blocked"
              value={countKey(summary?.counts, "customer_portal:blocked_cross_customer_add")}
              onClick={() => setFilter("customer_portal:blocked_cross_customer_add")}
              testId="alert-cross-block"
            />
            <SummaryCard
              icon={<Info size={18} weight="bold" />}
              tone="slate"
              label={`Total signals (${days}d)`}
              value={summary?.total || 0}
              onClick={() => setFilter("")}
              testId="alert-total"
            />
          </div>

          {/* detail list */}
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
              <div className="text-sm font-medium text-slate-900">
                Recent events {filter && <span className="text-slate-500 font-normal">— filtered by <code className="text-xs">{filter}</code></span>}
              </div>
              {filter && (
                <button onClick={() => setFilter("")} data-testid="portal-alerts-clear-filter"
                        className="text-xs text-slate-500 hover:text-slate-800">Clear filter</button>
              )}
            </div>
            <table className="w-full text-sm" data-testid="portal-alerts-events">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2">Customer</th>
                  <th className="text-left px-4 py-2">Timestamp</th>
                  <th className="text-left px-4 py-2">Source</th>
                  <th className="text-left px-4 py-2">Action taken</th>
                </tr>
              </thead>
              <tbody>
                {events.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-slate-500" data-testid="portal-alerts-empty">
                    No signals in this window.
                  </td></tr>
                )}
                {events.map((e, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`portal-alert-row-${i}`}>
                    <td className="px-4 py-2 text-slate-800">{e.event_label}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-800">{e.email_normalized || "—"}</td>
                    <td className="px-4 py-2 text-slate-700 text-xs">
                      {e.company_name || "—"}{" "}
                      {e.customer_id && <span className="text-slate-400 font-mono">({e.customer_id})</span>}
                    </td>
                    <td className="px-4 py-2 text-slate-600 font-mono text-xs">
                      {e.timestamp ? new Date(e.timestamp).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-2 text-slate-600 text-xs">{e.source || "system"}</td>
                    <td className="px-4 py-2 text-slate-700 text-xs">{e.action_taken}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ icon, tone, label, value, onClick, testId }) {
  const toneMap = {
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    orange: "bg-orange-50 text-orange-700 border-orange-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-left rounded-xl border p-4 hover:shadow-sm transition ${toneMap[tone] || toneMap.slate}`}
    >
      <div className="flex items-center justify-between">
        <div>{icon}</div>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
      </div>
      <div className="mt-2 text-xs font-medium">{label}</div>
    </button>
  );
}

function countKey(counts, k) { return (counts && counts[k]) || 0; }
function countMany(counts, keys) {
  if (!counts) return 0;
  return keys.reduce((n, k) => n + (counts[k] || 0), 0);
}
